import React, { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";

import { fetchCurrentUser, fetchWithAuth } from "../../../../api-client/APIClient";
import { showErrorToast, showSuccessToast } from "../../../shared/Toasts";
import useAppContext from "../../../context/useAppContext";
import { Form } from "react-bootstrap";
import { PaymentMethodTypes } from "./BillingModels";
import { navigateToBillingPage } from "../../../../router/RouterUtils";
import { useHistory } from "react-router-dom";
import { Button, ButtonVariant, Text } from "@merge-api/merge-javascript-shared";
import useTimeout from "../../../shared/hooks/useTimeout";

type Props = {
  onHide?: () => void;
  isFromUpgrade?: {
    fromUpgradeButton?: boolean;
  };
};

const BILLING_URI_PREFIX = "/billing";
const SLOW_PREPARE_TIMEOUT_MS = 15000;

function reportBillingError(error: unknown, operation: string) {
  Sentry.captureException(error, { tags: { operation } });
  try {
    posthog.captureException(error, { operation });
  } catch (err) {
    console.error("PostHog captureException failed:", err);
  }
}

// fetchWithAuth hands onError the raw Response rather than an Error, and both
// trackers stringify that to "[object Response]" with the status lost. See the
// onError signature in api-client/APIClient.tsx.
function reportBillingResponse(response: Response | undefined, operation: string) {
  if (response && (response.status === 401 || response.status === 403)) {
    return;
  }
  const status = response ? String(response.status) : "no response";
  reportBillingError(new Error(`${operation} failed: ${status}`), operation);
}

export default function CreditCardSetupForm(props: Props) {
  // state
  const [isLoading, setIsLoading] = useState(false);
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState<string>("");
  const [isCardReady, setIsCardReady] = useState(false);
  const [setupIntentFailed, setSetupIntentFailed] = useState(false);
  const [isSlow, setIsSlow] = useState(false);

  // hooks
  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();
  const { user, setUser } = useAppContext();
  const isMounted = useRef(true);

  // derived state
  const isSetupIntentPending = !setupIntentClientSecret && !setupIntentFailed;
  const isSubmitBlocked =
    !stripe || !elements || !isCardReady || isSetupIntentPending || setupIntentFailed;

  const blockedReason = setupIntentFailed
    ? "We could not start the card form. Reload the page to try again."
    : !isCardReady
    ? isSlow
      ? "The card field is taking longer than usual to load"
      : "Waiting for the card field to load"
    : isSlow
    ? "Setting up the card form is taking longer than usual"
    : "Setting up the card form";

  // effects
  // Tracks whether the form is still on screen so the awaited confirm below does
  // not set state after the parent closes the dialog.
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // The setup intent is what Stripe charges against, so Confirm stays blocked
  // until it arrives and a failure has to say so on screen.
  useEffect(() => {
    fetchWithAuth({
      path: "/billing/stripe/setup-intent",
      method: "POST",
      body: {
        payment_method: PaymentMethodTypes.CREDIT_CARD,
      },
      onResponse: (data) => {
        setSetupIntentClientSecret(data.setup_intent_client_secret);
      },
      onError: (response) => {
        reportBillingResponse(response, "stripe_setup_intent");
        setSetupIntentFailed(true);
        showErrorToast("We could not start the card form. Please reload the page and try again.");
      },
    });
  }, []);

  // CardElement has no load-error callback, unlike Stripe's PaymentElement, so a
  // field that never mounts and a setup intent that never lands both surface only
  // as Confirm staying blocked.
  useTimeout(
    () => {
      reportBillingError(
        new Error(
          isCardReady
            ? "Stripe setup intent never arrived"
            : "Stripe CardElement never became ready",
        ),
        isCardReady ? "stripe_setup_intent_stalled" : "stripe_card_element_load",
      );
      setIsSlow(true);
    },
    isSubmitBlocked && !setupIntentFailed ? SLOW_PREPARE_TIMEOUT_MS : null,
  );

  // event handlers
  const handleSubmit = async (event: React.FormEvent<HTMLElement>) => {
    event.preventDefault();

    if (isLoading || isSubmitBlocked) {
      return;
    }

    setIsLoading(true);

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setIsLoading(false);
      showErrorToast("Oops, something went wrong! Please try again in 10 seconds.");
      return;
    }

    let result;
    try {
      result = await stripe.confirmCardSetup(setupIntentClientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: user.name,
          },
        },
      });
    } catch (error) {
      reportBillingError(error, "stripe_confirm_card_setup");
      if (isMounted.current) {
        setIsLoading(false);
        showErrorToast("We could not save your card. Please try again.");
      }
      return;
    }

    if (!isMounted.current) {
      return;
    }

    if (result.error) {
      showErrorToast(result.error.message ?? "We could not save your card. Please try again.");
      setIsLoading(false);
      return;
    }

    showSuccessToast("Credit card added successfully!");

    if (props.onHide) {
      props.onHide();
    }
    if (props.isFromUpgrade?.fromUpgradeButton) {
      fetchWithAuth({
        path: `${BILLING_URI_PREFIX}/update-billing-status`,
        method: "POST",
        onResponse: () => {
          fetchCurrentUser(setUser);
          navigateToBillingPage(history, { refetch: "true", upgraded: "true" });
        },
        // The card is already saved and its setup intent is spent, so a second
        // Confirm would fail against Stripe. Land on billing rather than
        // offering a retry that cannot work.
        onError: (response) => {
          reportBillingResponse(response, "billing_update_status");
          showErrorToast("Your card was saved, but the upgrade did not finish. Please contact us.");
          fetchCurrentUser(setUser);
          navigateToBillingPage(history, { refetch: "true" });
        },
      });
      return;
    }

    if (
      props.onHide == null &&
      (props.isFromUpgrade == null || props.isFromUpgrade.fromUpgradeButton == false)
    ) {
      fetchCurrentUser(setUser);
      navigateToBillingPage(history, { refetch: "true" });
      return;
    }

    setIsLoading(false);
  };

  // render
  return (
    <Form className="w-100 mt-1">
      <div className="flex flex-col gap-2">
        <Text variant="h6">Enter your credit card</Text>
        <CardElement onReady={() => setIsCardReady(true)} />
      </div>
      {isSubmitBlocked && !isLoading && (
        <Text variant="sm" className={`mt-2 ${setupIntentFailed ? "text-red-60" : "text-gray-40"}`}>
          {blockedReason}
        </Text>
      )}
      <div className="flex gap-4 mt-4">
        <Button
          variant={ButtonVariant.SecondaryGray}
          fullWidth
          onClick={() => {
            navigateToBillingPage(history);
          }}
        >
          Cancel
        </Button>
        <Button
          disabled={isSubmitBlocked}
          loading={isLoading}
          onClick={(e) => {
            e.preventDefault();
            handleSubmit(e as React.FormEvent<HTMLElement>);
          }}
          fullWidth
        >
          Confirm
        </Button>
      </div>
    </Form>
  );
}
